import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { PlusCircle, Trash2 } from 'lucide-react';
import { LitLegionAdminTask, LitLegionNpc, LitLegionUserLevel } from '@/lib/api';

export interface CoinRewardEntry {
  coinId: string;
  coinName: string;
  quantity: number;
}

export interface BadgeRewardEntry {
  badgeId: string;
}

/**
 * Shape of a Lit-Legion task form. Used as the controlled `value` in
 * `TaskFormFields` and round-trips between Create and Edit pages with
 * the same field set.
 */
export interface TaskFormState {
  name: string;
  description: string;
  taskType: string;
  category: string;
  xpReward: number;
  npcId: string; // 'none' or an npc_<id>
  parentId: string; // 'none' or an ltk_<id>
  minLevelId: string; // 'none' or a lvl_<id>
  /**
   * Player visibility. `false` hides this quest from every player surface —
   * and, by cascade, every quest beneath it.
   */
  isActive: boolean;
  npcTextIntro: string;
  npcTextObjectives: string;
  npcTextInProgress: string;
  npcTextSuccess: string;
  coinRewards: CoinRewardEntry[];
  badgeRewards: BadgeRewardEntry[];
  /** Raw JSON for the public task definition. Empty string = NULL on submit. */
  definitionJson: string;
  /** Raw JSON for the admin-only answer key. Empty string = NULL on submit. */
  privateDefinitionJson: string;
}

export interface ParsedTaskFormContent {
  definition: Record<string, unknown> | null;
  privateDefinition: Record<string, unknown> | null;
  parseError: string | null;
}

/**
 * Parses the form's JSON fields. Sets `parseError` (with the field name
 * prepended) on the first JSON parse failure; the caller short-circuits on
 * a non-null `parseError`. Server-side Zod validation runs after a clean
 * parse — this is just a syntax check so authors don't pay a network
 * round-trip on a trailing comma.
 */
export function parseTaskFormContent(form: TaskFormState): ParsedTaskFormContent {
  const out: ParsedTaskFormContent = {
    definition: null,
    privateDefinition: null,
    parseError: null,
  };
  const def = form.definitionJson.trim();
  if (def.length > 0) {
    try {
      const parsed = JSON.parse(def);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        out.definition = parsed as Record<string, unknown>;
      } else {
        out.parseError = 'Definition must be a JSON object';
        return out;
      }
    } catch (err) {
      out.parseError = `Definition JSON: ${err instanceof Error ? err.message : 'parse error'}`;
      return out;
    }
  }
  const priv = form.privateDefinitionJson.trim();
  if (priv.length > 0) {
    try {
      const parsed = JSON.parse(priv);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        out.privateDefinition = parsed as Record<string, unknown>;
      } else {
        out.parseError = 'Private definition must be a JSON object';
        return out;
      }
    } catch (err) {
      out.parseError = `Private definition JSON: ${err instanceof Error ? err.message : 'parse error'}`;
      return out;
    }
  }
  return out;
}

export function serializeContentToJson(value: Record<string, unknown> | null | undefined): string {
  if (value == null) return '';
  return JSON.stringify(value, null, 2);
}

export const EMPTY_TASK_FORM_STATE: TaskFormState = {
  name: '',
  description: '',
  taskType: 'standard',
  category: '',
  xpReward: 0,
  npcId: 'none',
  parentId: 'none',
  minLevelId: 'none',
  isActive: true,
  npcTextIntro: '',
  npcTextObjectives: '',
  npcTextInProgress: '',
  npcTextSuccess: '',
  coinRewards: [],
  badgeRewards: [],
  definitionJson: '',
  privateDefinitionJson: '',
};

interface TaskFormFieldsProps {
  value: TaskFormState;
  onChange: (next: TaskFormState) => void;
  /**
   * The list of tasks shown in the parent-task select. Callers pre-filter
   * to exclude self + descendants for edit flows; pass the full list for
   * create flows.
   */
  parentTaskOptions: LitLegionAdminTask[];
  parentTasksLoading: boolean;
  npcs: LitLegionNpc[];
  npcsLoading: boolean;
  levels: LitLegionUserLevel[];
  levelsLoading: boolean;
  /**
   * True when an ANCESTOR of this task is inactive, so the task is hidden from
   * players regardless of its own switch. Server-computed; the form only
   * renders it.
   */
  ancestorInactive?: boolean;
}

export function TaskFormFields({
  value,
  onChange,
  parentTaskOptions,
  parentTasksLoading,
  npcs,
  npcsLoading,
  levels,
  levelsLoading,
  ancestorInactive = false,
}: TaskFormFieldsProps) {
  const set = <K extends keyof TaskFormState>(key: K, v: TaskFormState[K]) =>
    onChange({ ...value, [key]: v });

  const updateCoinReward = (
    index: number,
    field: keyof CoinRewardEntry,
    fieldValue: string | number,
  ) => {
    set(
      'coinRewards',
      value.coinRewards.map((entry, i) =>
        i === index ? { ...entry, [field]: fieldValue } : entry,
      ),
    );
  };

  const updateBadgeReward = (index: number, badgeId: string) => {
    set(
      'badgeRewards',
      value.badgeRewards.map((entry, i) => (i === index ? { badgeId } : entry)),
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={value.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Enter task name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={value.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Describe the task..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="task-is-active">Active</Label>
            <div className="flex items-center gap-3">
              <Switch
                id="task-is-active"
                checked={value.isActive}
                onCheckedChange={(checked) => set('isActive', checked)}
              />
              <span className="text-sm text-muted-foreground">
                Inactive quests are hidden from players everywhere, along with every quest beneath
                them. Player progress is kept and returns when you switch this back on.
              </span>
            </div>
            {/* Without this, switching Active on here appears to do nothing:
                the quest stays hidden because something above it is off. */}
            {ancestorInactive && (
              <p className="text-sm text-destructive">
                This quest is still hidden from players because a quest above it in the tree is
                inactive. Turning this switch on will not reveal it — find the inactive ancestor in
                the{' '}
                <Link to="/a/arche_lit_legion/admin/tasks/tree" className="underline">
                  task tree
                </Link>{' '}
                and reactivate that one.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="taskType">Task Type</Label>
              <Select value={value.taskType} onValueChange={(v) => set('taskType', v)}>
                <SelectTrigger id="taskType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="multiple_question">Multiple Question</SelectItem>
                  <SelectItem value="check_inventory">Check Inventory</SelectItem>
                  <SelectItem value="user_input">User Input</SelectItem>
                  <SelectItem value="initial">Initial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={value.category}
                onChange={(e) => set('category', e.target.value)}
                placeholder="e.g., combat, exploration"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="xpReward">XP Reward</Label>
            <Input
              id="xpReward"
              type="number"
              min={0}
              value={value.xpReward}
              onChange={(e) => set('xpReward', parseInt(e.target.value, 10) || 0)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="npcId">NPC</Label>
            <Select value={value.npcId} onValueChange={(v) => set('npcId', v)}>
              <SelectTrigger id="npcId">
                <SelectValue
                  placeholder={npcsLoading ? 'Loading NPCs...' : 'Select NPC (optional)'}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No NPC</SelectItem>
                {npcs.map((npc) => (
                  <SelectItem key={npc.npcId} value={npc.npcId}>
                    {npc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="parentId">Parent Task</Label>
            <Select value={value.parentId} onValueChange={(v) => set('parentId', v)}>
              <SelectTrigger id="parentId">
                <SelectValue
                  placeholder={
                    parentTasksLoading ? 'Loading tasks...' : 'Select parent task (optional)'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No parent task</SelectItem>
                {parentTaskOptions.map((t) => (
                  <SelectItem key={t.taskId} value={t.taskId}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="minLevelId">Minimum Level</Label>
            <Select value={value.minLevelId} onValueChange={(v) => set('minLevelId', v)}>
              <SelectTrigger id="minLevelId">
                <SelectValue
                  placeholder={
                    levelsLoading ? 'Loading levels...' : 'Select minimum level (optional)'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No level requirement</SelectItem>
                {levels.map((level) => (
                  <SelectItem key={level.levelId} value={level.levelId}>
                    Level {level.levelNumber} - {level.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">NPC Text</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="npcTextIntro">Introduction Text</Label>
            <Textarea
              id="npcTextIntro"
              value={value.npcTextIntro}
              onChange={(e) => set('npcTextIntro', e.target.value)}
              placeholder="What the NPC says when the player first encounters this task..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="npcTextObjectives">Objectives Text</Label>
            <Textarea
              id="npcTextObjectives"
              value={value.npcTextObjectives}
              onChange={(e) => set('npcTextObjectives', e.target.value)}
              placeholder="Description of what the player needs to do..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="npcTextInProgress">In Progress Text</Label>
            <Textarea
              id="npcTextInProgress"
              value={value.npcTextInProgress}
              onChange={(e) => set('npcTextInProgress', e.target.value)}
              placeholder="What the NPC says while the task is in progress..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="npcTextSuccess">Success Text</Label>
            <Textarea
              id="npcTextSuccess"
              value={value.npcTextSuccess}
              onChange={(e) => set('npcTextSuccess', e.target.value)}
              placeholder="What the NPC says when the task is completed..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Task Content (advanced)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Definition is the JSON payload rendered to the player (text, questions, inventory
            checks, etc.). Private definition holds the answer key for graded multiple-choice
            questions and is never sent to players. Leave blank for tasks that don&apos;t require
            structured interaction. The server validates the schema; see{' '}
            <code className="text-xs">docs/lit-legion-task-definitions.md</code>.
          </p>
          <p className="text-xs text-muted-foreground">
            <strong>Images</strong> must be a relative storage key uploaded to the DFE Icons arche
            &mdash; e.g. <code className="text-xs">/dfe-icons/LitLegion/Meme-1.jpg</code>. Do not
            paste a CDN or third-party URL: a stored host pins the row to one environment, and only
            a relative key can be served inside a plugin sandbox. Filenames may use letters, digits,{' '}
            <code className="text-xs">.</code> <code className="text-xs">_</code>{' '}
            <code className="text-xs">-</code> only &mdash; no spaces.
          </p>
          <div className="space-y-2">
            <Label htmlFor="definitionJson">Definition (JSON)</Label>
            <Textarea
              id="definitionJson"
              value={value.definitionJson}
              onChange={(e) => set('definitionJson', e.target.value)}
              placeholder='{"elements":[{"kind":"text","index":0,"text":"..."}]}'
              rows={8}
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="privateDefinitionJson">Private definition (JSON)</Label>
            <Textarea
              id="privateDefinitionJson"
              value={value.privateDefinitionJson}
              onChange={(e) => set('privateDefinitionJson', e.target.value)}
              placeholder='{"answers":[{"questionId":"q1","optionId":"a"}]}'
              rows={6}
              className="font-mono text-xs"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Coin Rewards</CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                set('coinRewards', [
                  ...value.coinRewards,
                  { coinId: '', coinName: '', quantity: 1 },
                ])
              }
            >
              <PlusCircle className="h-4 w-4 mr-1" />
              Add Coin
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {value.coinRewards.length === 0 ? (
            <p className="text-sm text-muted-foreground">No coin rewards added.</p>
          ) : (
            value.coinRewards.map((coin, index) => (
              <div key={index} className="flex items-end gap-3">
                <div className="flex-1 space-y-1">
                  <Label>Coin ID</Label>
                  <Input
                    value={coin.coinId}
                    onChange={(e) => updateCoinReward(index, 'coinId', e.target.value)}
                    placeholder="e.g., coin_gold"
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <Label>Display Name</Label>
                  <Input
                    value={coin.coinName}
                    onChange={(e) => updateCoinReward(index, 'coinName', e.target.value)}
                    placeholder="e.g., Gold"
                  />
                </div>
                <div className="w-24 space-y-1">
                  <Label>Qty</Label>
                  <Input
                    type="number"
                    min={1}
                    value={coin.quantity}
                    onChange={(e) =>
                      updateCoinReward(index, 'quantity', parseInt(e.target.value, 10) || 1)
                    }
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive shrink-0"
                  onClick={() =>
                    set(
                      'coinRewards',
                      value.coinRewards.filter((_, i) => i !== index),
                    )
                  }
                  aria-label="Remove coin reward"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Badge Rewards</CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => set('badgeRewards', [...value.badgeRewards, { badgeId: '' }])}
            >
              <PlusCircle className="h-4 w-4 mr-1" />
              Add Badge
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {value.badgeRewards.length === 0 ? (
            <p className="text-sm text-muted-foreground">No badge rewards added.</p>
          ) : (
            value.badgeRewards.map((badge, index) => (
              <div key={index} className="flex items-end gap-3">
                <div className="flex-1 space-y-1">
                  <Label>Badge ID</Label>
                  <Input
                    value={badge.badgeId}
                    onChange={(e) => updateBadgeReward(index, e.target.value)}
                    placeholder="e.g., badge_first_quest"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive shrink-0"
                  onClick={() =>
                    set(
                      'badgeRewards',
                      value.badgeRewards.filter((_, i) => i !== index),
                    )
                  }
                  aria-label="Remove badge reward"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </>
  );
}

/**
 * Returns the set of `taskId`s that are descendants of `rootTaskId` in the
 * forest implied by the flat task list. Used by the edit page to exclude
 * self + descendants from the parent-select, preventing UI-introduced cycles.
 */
export function getDescendantTaskIds(
  rootTaskId: string,
  allTasks: LitLegionAdminTask[],
): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const t of allTasks) {
    if (t.parentId) {
      const list = childrenByParent.get(t.parentId);
      if (list) list.push(t.taskId);
      else childrenByParent.set(t.parentId, [t.taskId]);
    }
  }
  const descendants = new Set<string>();
  const stack = [rootTaskId];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    const kids = childrenByParent.get(id);
    if (!kids) continue;
    for (const kid of kids) {
      if (!descendants.has(kid)) {
        descendants.add(kid);
        stack.push(kid);
      }
    }
  }
  return descendants;
}
